import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap/nav';
import { TranslatePipe } from '@ngx-translate/core';
import {
  PasTabContentDirective,
  PasTabDirective,
  PasTabsetComponent,
  PasTabTitleDirective,
} from '@porsche-aftersales/frontend-toolbox';
import { Subscription } from 'rxjs';

import { isEditable } from '../../services/booking/recourse-booking-functions';
import { RecourseBookingStore } from '../../services/booking/recourse-booking-store';
import { UserService } from '../../services/user-service/user.service';

import { RecourseBookingAddEditDetailsComponent } from './recourse-booking-add-edit-details/recourse-booking-add-edit-details.component';
import { RecourseBookingComponent } from './recourse-booking-overview/recourse-booking.component';
import { RecourseBookingUrlConstants } from './recourse-booking-url-constants';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'recourse-booking-tabset',
  templateUrl: './recourse-booking-tabset.component.html',
  styleUrls: ['./recourse-booking-tabset.component.scss'],
  imports: [
    RecourseBookingComponent,
    PasTabDirective,
    RecourseBookingAddEditDetailsComponent,
    PasTabsetComponent,
    TranslatePipe,
    PasTabTitleDirective,
    PasTabContentDirective,
  ],
})
export class RecourseBookingTabsetComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly userService = inject(UserService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);

  @ViewChild(PasTabsetComponent)
  public tabset: PasTabsetComponent;

  @ViewChild(RecourseBookingAddEditDetailsComponent)
  public addEditDetails: RecourseBookingAddEditDetailsComponent;

  public readonly BOOKING_OVERVIEW_TAB: string = RecourseBookingUrlConstants.OVERVIEW_TAB;
  public readonly DETAILS_TAB: string = RecourseBookingUrlConstants.DETAILS_TAB;
  public readonly EDIT_TAB: string = RecourseBookingUrlConstants.EDIT_TAB;
  public readonly NEW_TAB: string = RecourseBookingUrlConstants.NEW_TAB;

  public detailsTabDisabled = true;
  public detailsTabHidden = true;
  public editTabDisabledAndHidden = true;
  public newTabDisabledAndHidden = true;

  public routerParamName = 'tabId';
  private pathParamSubscription: Subscription;

  public ngOnInit(): void {
    const subscribeToRouter = (): void => {
      this.pathParamSubscription = this.route.params.subscribe(params => {
        const tabId = params.tabId;
        this.initTabParams(tabId);
      });
    };

    subscribeToRouter();
  }

  private async initTabParams(tabId: string): Promise<void> {
    this.setTabVisibility(tabId);
    // wait for ui-framework tabset component to be initialized (!)
    await Promise.resolve(this.tabset);
    if (this.tabset.activeId === tabId) {
      if (this.addEditDetails) {
        await this.addEditDetails.initializeView();
      }
    } else {
      this.openTab(tabId);
    }
  }

  public ngAfterViewInit(): void {
    this.cdRef.detectChanges(); // prevent angular error in dev mode
  }

  public onChangingTab(event: NgbNavChangeEvent): void {
    event.preventDefault();
  }

  public openTab(tabId: string): void {
    Promise.resolve(this.tabset.tabs).then(() => {
      this.tabset.activeId = tabId;
      this.setTabVisibility(tabId);
      this.tabset.select(tabId);
      this.tabset.changeTabSize();
    });
  }

  public setTabVisibility(tabId: string): void {
    if (tabId === this.BOOKING_OVERVIEW_TAB) {
      this.detailsTabDisabled = true;
      this.detailsTabHidden = true;
      this.editTabDisabledAndHidden = true;
      this.newTabDisabledAndHidden = true;
    }
    if (tabId === this.DETAILS_TAB) {
      this.detailsTabDisabled = false;
      this.detailsTabHidden = false;
      this.editTabDisabledAndHidden = !isEditable(
        this.recourseBookingStore.selectedRecourseBooking,
        this.userService.getUser(),
        true,
      );
    }
    if (tabId === this.EDIT_TAB) {
      this.detailsTabDisabled = false;
      this.detailsTabHidden = false;
      this.editTabDisabledAndHidden = false;
      this.newTabDisabledAndHidden = true;
    }
    if (tabId === this.NEW_TAB) {
      this.detailsTabDisabled = true;
      this.detailsTabHidden = true;
      this.editTabDisabledAndHidden = true;
      this.newTabDisabledAndHidden = false;
    }
  }

  public ngOnDestroy(): void {
    this.pathParamSubscription.unsubscribe();
  }

  public userHasAnyRight(): boolean {
    return this.userService.getRights().length > 0;
  }

  public isOverviewTabActive(): boolean {
    return this.tabset && this.tabset.activeId === this.BOOKING_OVERVIEW_TAB;
  }
}
